/**
 * AiCallSummary — render the LLM-generated call summary (`Call.ai_summary`).
 *
 * Two flavours:
 *   - ``variant="row"``  → compact one-liner + intent/sentiment badges. Used
 *     in the phone-log list rows to preview each call at a glance.
 *   - ``variant="card"`` → full card with one-liner, key points, action items,
 *     and metadata pills. Used in the call-detail drawer above the raw
 *     transcript.
 *
 * Also handles the three non-completed states:
 *   - ``pending``   → subtle "waiting to run" chip
 *   - ``generating``→ spinning "AI is drafting…" state
 *   - ``failed``    → "Summary unavailable" + optional Retry click
 */
import React from "react";
import { Loader2, RefreshCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const SENTIMENT_META = {
  positive: { emoji: "😊", label: "Positive", bg: "bg-emerald-50", fg: "text-emerald-800", ring: "ring-emerald-200" },
  neutral:  { emoji: "😐", label: "Neutral",  bg: "bg-slate-50",   fg: "text-slate-700",   ring: "ring-slate-200"   },
  negative: { emoji: "😔", label: "Negative", bg: "bg-rose-50",    fg: "text-rose-800",    ring: "ring-rose-200"    },
};

const INTENT_LABELS = {
  inventory_inquiry:   "Inventory inquiry",
  appointment_request: "Appointment request",
  financing_question:  "Financing question",
  trade_in_inquiry:    "Trade-in inquiry",
  existing_appointment:"Existing appointment",
  other:               "Other",
};

const scoreColor = (score) => {
  if (score >= 70) return "bg-emerald-100 text-emerald-800";
  if (score >= 40) return "bg-amber-100 text-amber-800";
  return "bg-rose-100 text-rose-800";
};

export const AiCallSummary = ({
  call,
  variant = "card",
  onRetry,
  compact = false,
}) => {
  const status = call?.ai_summary_status || "pending";
  const summary = call?.ai_summary;

  // ---- Non-completed states ---------------------------------------------
  if (status !== "completed" || !summary) {
    if (status === "generating") {
      return (
        <div
          className="flex items-center gap-2 text-xs text-slate-500"
          data-testid="ai-summary-generating"
        >
          <Loader2 size={12} className="animate-spin" /> AI is drafting a summary…
        </div>
      );
    }
    if (status === "failed") {
      return (
        <div
          className="flex flex-wrap items-center gap-2 text-xs text-rose-700"
          data-testid="ai-summary-failed"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-1 font-semibold">
            Summary unavailable
          </span>
          {call?.ai_summary_error && (
            <span className="text-slate-500 truncate max-w-[240px]" title={call.ai_summary_error}>
              {call.ai_summary_error}
            </span>
          )}
          {onRetry && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
              onClick={() => onRetry(call.id)}
              data-testid="ai-summary-retry-btn"
            >
              <RefreshCcw size={11} /> Retry
            </Button>
          )}
        </div>
      );
    }
    // pending / unavailable
    return (
      <div
        className="text-xs text-slate-400 italic"
        data-testid="ai-summary-pending"
      >
        Summary pending…
      </div>
    );
  }

  const sentiment = SENTIMENT_META[summary.sentiment] || SENTIMENT_META.neutral;

  // ---- Compact row variant (list rows on phone log) ----------------------
  if (variant === "row") {
    return (
      <div
        className="flex items-start gap-2 text-xs text-slate-600"
        data-testid={`ai-summary-row-${call.id}`}
      >
        <Sparkles size={11} className="shrink-0 mt-0.5 text-teal-600" />
        <span className="flex-1 truncate">{summary.one_line}</span>
        <span
          className={`ml-2 shrink-0 rounded-full ring-1 px-1.5 py-0.5 font-semibold ${sentiment.bg} ${sentiment.fg} ${sentiment.ring}`}
          title={`Sentiment: ${sentiment.label}`}
        >
          {sentiment.emoji}
        </span>
        <span
          className={`ml-1 shrink-0 rounded-full px-1.5 py-0.5 font-semibold ${scoreColor(summary.intent_score)}`}
          title="Conversion likelihood (1-100)"
          data-testid={`ai-summary-score-${call.id}`}
        >
          {summary.intent_score}
        </span>
      </div>
    );
  }

  // ---- Full card variant --------------------------------------------------
  return (
    <div
      className="rounded-xl border border-teal-100 bg-teal-50/40 px-4 py-3.5"
      data-testid="ai-summary-card"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-teal-700 font-semibold">
        <Sparkles size={12} /> AI summary
      </div>
      <p className="mt-1.5 font-display font-semibold text-slate-900 leading-snug" data-testid="ai-summary-oneline">
        {summary.one_line}
      </p>

      <div className="flex flex-wrap gap-1.5 mt-3">
        <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-sky-100 text-sky-800">
          {INTENT_LABELS[summary.customer_intent] || "Other"}
        </span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${sentiment.bg} ${sentiment.fg}`}>
          {sentiment.emoji} {sentiment.label}
        </span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${scoreColor(summary.intent_score)}`}>
          Score {summary.intent_score}/100
        </span>
      </div>

      {!compact && summary.key_points?.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Key points</p>
          <ul className="mt-1 space-y-1 text-sm text-slate-700 list-disc pl-4">
            {summary.key_points.map((k, i) => <li key={i}>{k}</li>)}
          </ul>
        </div>
      )}

      {!compact && summary.action_items?.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Action items</p>
          <ul className="mt-1 space-y-1 text-sm text-slate-700 list-disc pl-4">
            {summary.action_items.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      {call?.ai_summary?.demo && (
        <p className="mt-3 text-[10px] text-slate-400 italic">Seeded demo summary — real transcripts use gpt-5.4.</p>
      )}
    </div>
  );
};

export default AiCallSummary;
